import * as React from "react";
import { css } from "@emotion/core";
import { Button, Callout, Tooltip } from "@blueprintjs/core";
import { FILE_AGE_WARNING_DAYS } from "components/Orders/utils/validation";

interface UploadStatusIconProps {
  completed: boolean;
  active: boolean;
  uploadInProgress: boolean;
  fileAvailable: boolean;
  isOld: boolean;
  type: "image" | "pointcloud" | "geometry";
  onClick?: () => void;
}

export function UploadStatusIcon({
  completed,
  active,
  uploadInProgress,
  fileAvailable,
  isOld,
  type,
  onClick,
}: UploadStatusIconProps) {
  const getIcon = () => {
    if (completed) return "cloud-tick";
    if (active) return "cloud-upload";
    if (uploadInProgress) return "time";
    if (fileAvailable === false) return "help";
    if (isOld) return "warning-sign";
    if (fileAvailable) {
      switch (type) {
        case "image":
          return "media";
        case "pointcloud":
          return "antenna";
        case "geometry":
          return "shapes";
        default:
          return "media";
      }
    }
    return "help";
  };

  const getIntent = () => {
    if (completed) return "success";
    if (active) return "primary";
    if (fileAvailable === false) return "danger";
    if (isOld) return "warning";
    if (fileAvailable) return undefined;
  };

  const getStatusMessage = () => {
    const itemName =
      type === "image"
        ? "Image"
        : type === "pointcloud"
        ? "Point cloud"
        : "Layer/Accessory";

    if (completed) return `${itemName} is uploaded`;
    if (active) return `${itemName} is being uploaded`;
    if (uploadInProgress) return `${itemName} is pending upload`;
    if (fileAvailable === false)
      return `${itemName} is not available for upload`;
    if (isOld) return `${itemName} is older than ${FILE_AGE_WARNING_DAYS} days`;
    if (fileAvailable) return `${itemName} is available for upload`;
    return `${itemName} is not available for upload`;
  };

  const getCalloutIntent = () => {
    if (completed) return "success";
    if (active) return "primary";
    if (uploadInProgress) return "warning";
    if (fileAvailable === false) return "danger";
    if (isOld) return "warning";
    if (fileAvailable) return "success";
  };

  return (
    <Tooltip
      inheritDarkTheme={false}
      portalClassName="upload-tooltip"
      content={
        <div>
          <Callout intent={getCalloutIntent()} minimal>
            <span
              css={css`
                font-weight: 700;
                color: var(--neutral-3);
              `}
            >
              {getStatusMessage()}
            </span>
          </Callout>
        </div>
      }
    >
      <Button
        size="large"
        icon={getIcon()}
        intent={getIntent()}
        onClick={completed || active || uploadInProgress ? undefined : onClick}
      />
    </Tooltip>
  );
}
